import { useState, type FormEvent } from 'react'
import Logo from './Logo'
import SocialLinks from './SocialLinks'

const features = [
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    text: 'Quick risk snapshot for new tokens',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
    text: 'Dev/insider holding visibility',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    text: 'Migration + liquidity checks',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
    text: 'Alerts and filters tailored for degen traders',
  },
]

export default function ComingSoonCard() {
  const [email, setEmail] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState('')

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      return
    }

    // Placeholder handler - no actual submission
    console.log('Email submitted (placeholder):', email)
    setIsSubmitted(true)
    setEmail('')
  }

  return (
    <div className="card p-8 md:p-12 max-w-2xl w-full mx-4 shadow-glow">
      {/* Logo */}
      <div className="flex justify-center mb-8">
        <Logo size="lg" />
      </div>

      {/* Heading */}
      <h1 className="text-3xl md:text-4xl font-bold text-center mb-4 text-balance">
        <span className="gradient-text">RugScan</span> is coming soon
      </h1>

      {/* Tagline */}
      <p className="text-lg md:text-xl text-center text-muted mb-2">
        Smarter memecoin due diligence.
      </p>
      <p className="text-lg md:text-xl text-center text-primary-400 font-medium mb-8">
        Before they rug you.
      </p>

      {/* Domain */}
      <div className="flex justify-center mb-8">
        <span className="px-4 py-2 rounded-full bg-surface border border-surface-border text-sm font-mono text-muted">
          rugscan.me
        </span>
      </div>

      {/* Description */}
      <p className="text-center text-muted mb-8 leading-relaxed max-w-lg mx-auto">
        RugScan will help you quickly assess new memecoins with on-chain data,
        dev holdings, migrations, and more—before you ape in.
      </p>

      {/* Features List */}
      <div className="mb-10">
        <h2 className="text-sm font-semibold text-muted uppercase tracking-wider mb-4 text-center">
          What's Coming
        </h2>
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li
              key={index}
              className="flex items-center gap-3 text-white/90"
            >
              <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary-400">
                {feature.icon}
              </span>
              <span className="text-sm md:text-base">{feature.text}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Email Capture */}
      <div className="mb-8">
        <h2 className="text-sm font-semibold text-muted uppercase tracking-wider mb-4 text-center">
          Get Early Access
        </h2>

        {isSubmitted ? (
          <div className="text-center p-4 rounded-lg bg-accent/10 border border-accent/20">
            <p className="text-accent font-medium">
              Thanks! We'll notify you when we launch.
            </p>
            {/* <p className="text-xs text-muted mt-2">
              (This is a placeholder—no email was actually saved)
            </p> */}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  aria-label="Email address for early access"
                  aria-describedby={error ? 'email-error' : undefined}
                  className={`input-field w-full ${error ? 'border-danger focus:ring-danger' : ''}`}
                />
              </div>
              <button
                type="submit"
                className="btn-primary whitespace-nowrap"
              >
                Notify Me at Launch
              </button>
            </div>
            {error && (
              <p id="email-error" className="text-danger text-sm" role="alert">
                {error}
              </p>
            )}
            <p className="text-xs text-muted text-center">
              No spam. Just alpha when we launch.
            </p>
            {/* <p className="text-xs text-muted/60 text-center italic">
              Note: This form is a placeholder—no backend is connected yet.
            </p> */}
          </form>
        )}
      </div>

      {/* Social Links */}
      <div className="pt-6 border-t border-surface-border">
        <div className="flex justify-center">
          <SocialLinks />
        </div>
      </div>
    </div>
  )
}
